import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BusinessProvider } from "@/contexts/BusinessContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { BrandProvider } from "@/contexts/BrandContext";
import { Toaster } from "@/components/ui/sonner";
import AdminLayout from "@/layouts/AdminLayout";
import ClientLayout from "@/layouts/ClientLayout";

// Login screens stay eager — they're tiny and part of the very first paint.
import Login from "@/pages/Login";
import ClientLogin from "@/pages/ClientLogin";

// Everything else is code-split so the initial JS bundle stays small.
// Users only download the pages they actually navigate to → much faster first paint,
// especially on slow / mobile networks (the main pain point in production).
const Dashboard          = lazy(() => import("@/pages/Dashboard"));
const Leads              = lazy(() => import("@/pages/Leads"));
const LeadDetail         = lazy(() => import("@/pages/LeadDetail"));
const Followups          = lazy(() => import("@/pages/Followups"));
const Clients            = lazy(() => import("@/pages/Clients"));
const Tickets            = lazy(() => import("@/pages/Tickets"));
const TicketDetail       = lazy(() => import("@/pages/TicketDetail"));
const Meetings           = lazy(() => import("@/pages/Meetings"));
const Employees          = lazy(() => import("@/pages/Employees"));
const Reports            = lazy(() => import("@/pages/Reports"));
const Businesses         = lazy(() => import("@/pages/Businesses"));
const Notifications      = lazy(() => import("@/pages/Notifications"));
const AuditLog           = lazy(() => import("@/pages/AuditLog"));
const ActivityLog        = lazy(() => import("@/pages/ActivityLog"));
const Templates          = lazy(() => import("@/pages/Templates"));
const StatusesSettings   = lazy(() => import("@/pages/StatusesSettings"));
const Chat               = lazy(() => import("@/pages/Chat"));
const Profile            = lazy(() => import("@/pages/Profile"));
const IntegrationCenter  = lazy(() => import("@/pages/IntegrationCenter"));
const Roles              = lazy(() => import("@/pages/Roles"));
const SystemBranding     = lazy(() => import("@/pages/SystemBranding"));
const FacebookAccounts   = lazy(() => import("@/pages/FacebookAccounts"));
const SystemHealth       = lazy(() => import("@/pages/SystemHealth"));

const ClientDashboard    = lazy(() => import("@/pages/client/ClientDashboard"));
const ClientTickets      = lazy(() => import("@/pages/client/ClientTickets"));
const ClientMeetings     = lazy(() => import("@/pages/client/ClientMeetings"));
const ClientProfile      = lazy(() => import("@/pages/client/ClientProfile"));
const ClientChat         = lazy(() => import("@/pages/client/ClientChat"));

function PageFallback() {
  return (
    <div className="h-64 w-full flex items-center justify-center">
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <div className="h-4 w-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        Loading…
      </div>
    </div>
  );
}

function Protected({ children, kindRequired = "user" }) {
  const { user, loading, kind } = useAuth();
  if (loading) return <div className="h-screen w-full flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to={kindRequired === "client" ? "/portal" : "/login"} replace />;
  if (kind !== kindRequired) return <Navigate to={kindRequired === "client" ? "/portal" : "/login"} replace />;
  return children;
}

// If a client somehow lands on /login, kick them to /portal immediately.
function StaffLoginGate() {
  const { user, loading, kind } = useAuth();
  if (loading) return null;
  if (user && kind === "client") return <Navigate to="/portal" replace />;
  if (user && kind === "user") return <Navigate to="/dashboard" replace />;
  return <Login />;
}

function ClientLoginGate() {
  const { user, loading, kind } = useAuth();
  if (loading) return null;
  if (user && kind === "user") return <Navigate to="/dashboard" replace />;
  if (user && kind === "client") return <Navigate to="/client/dashboard" replace />;
  return <ClientLogin />;
}

function RootRedirect() {
  const { user, loading, kind } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={kind === "client" ? "/client/dashboard" : "/dashboard"} replace />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<StaffLoginGate />} />
        <Route path="/portal" element={<ClientLoginGate />} />
        <Route path="/client-login" element={<Navigate to="/portal" replace />} />

        <Route element={<Protected><AdminLayout /></Protected>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/leads" element={<Leads />} />
          <Route path="/leads/status/:statusName" element={<Leads />} />
          <Route path="/leads/:id" element={<LeadDetail />} />
          <Route path="/followups" element={<Followups />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/tickets" element={<Tickets />} />
          <Route path="/tickets/:id" element={<TicketDetail />} />
          <Route path="/meetings" element={<Meetings />} />
          <Route path="/employees" element={<Employees />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/audit" element={<AuditLog />} />
          <Route path="/activity-log" element={<ActivityLog />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/settings/integrations" element={<IntegrationCenter />} />
          <Route path="/settings/facebook" element={<FacebookAccounts />} />
          <Route path="/settings/businesses" element={<Businesses />} />
          <Route path="/settings/templates" element={<Templates />} />
          <Route path="/settings/statuses" element={<StatusesSettings />} />
          <Route path="/settings/roles" element={<Roles />} />
          <Route path="/settings/branding" element={<SystemBranding />} />
          <Route path="/settings/health" element={<SystemHealth />} />
        </Route>

        <Route element={<Protected kindRequired="client"><ClientLayout /></Protected>}>
          <Route path="/client" element={<Navigate to="/client/dashboard" replace />} />
          <Route path="/client/dashboard" element={<ClientDashboard />} />
          <Route path="/client/tickets" element={<ClientTickets />} />
          <Route path="/client/chat" element={<ClientChat />} />
          <Route path="/client/meetings" element={<ClientMeetings />} />
          <Route path="/client/profile" element={<ClientProfile />} />
        </Route>

        <Route path="/" element={<RootRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <BrandProvider>
          <AuthProvider>
            <BusinessProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </BusinessProvider>
          </AuthProvider>
        </BrandProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
